import { createContext, useContext } from 'react';
import type { OrganizationOption } from './auth';
import type { ProjectRef, SessionUser } from './types';

export interface PortalContextValue {
  user: SessionUser;
  /** Every project this user may see. A client user has exactly one. */
  projects: ProjectRef[];
  /**
   * Null when this organization holds no project the viewer may see. Only staff ever observe
   * that: they start in the internal organization, which is the roster and never holds
   * projects, and they reach the rest of the deployment through the switcher in the shell.
   * Screens that read a project guard on it; the shell itself must not.
   */
  activeProject: ProjectRef | null;
  setActiveProjectId: (projectId: string) => void;
  /**
   * Every organization the viewer belongs to. A client user has exactly one; staff have one
   * per client, because reconciliation gives them standing in all of them.
   */
  organizations: OrganizationOption[];
  /**
   * Whether that list is still being fetched. It is NOT part of the shell's loading gate — a
   * slow lookup costs the switcher and nothing else — so an empty list means "not loaded yet"
   * as often as it means "nothing there", and the issue route has to tell those apart: it
   * decides between switching tenant and answering not-found on whether the URL's organisation
   * is in this list, and answering not-found early would 404 a link that was about to work.
   */
  organizationsPending: boolean;
  /**
   * Switch tenant. Reloads the document into the new organization rather than re-fetching in
   * place — see the note on the implementation. `destination` is where to land, defaulting to
   * the board; the current route may not exist in the organization being switched to.
   */
  setActiveOrganizationId: (organizationId: string, destination?: string) => void;
  /**
   * Why the last tenant switch did not happen, or null.
   *
   * Lives on the context because the picker is in `AppShell` and the switch is driven from
   * `AuthenticatedShell`; without it a refusal has nowhere to appear, which is what let a
   * failed switch reload back onto the previous client in silence.
   */
  organizationSwitchError: string | null;
  /**
   * Whether this deployment has a board to receive feedback about Portal itself, as reported by
   * `GET /api/me`. `FeedbackWidget` draws nothing when it is false — a control that takes a
   * written report and drops it is worse than an absent one.
   */
  feedbackAvailable: boolean;
  /** Whether this deployment serves in-app notifications. False draws no bell at all. */
  notificationsAvailable: boolean;
  /**
   * Where this deployment's MCP endpoint lives, as reported by `GET /api/me`, or null when the
   * API did not say. The profile page draws connection instructions only when it has a real
   * address to print — a placeholder is worse than an absence, because somebody pastes it.
   */
  mcpEndpoint: string | null;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function PortalProvider({
  value,
  children,
}: {
  value: PortalContextValue;
  children: React.ReactNode;
}) {
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext);
  // Throwing beats returning a placeholder: a page rendered outside the shell would
  // otherwise query with an empty project id and quietly show an empty board.
  if (!value) throw new Error('usePortal must be used inside the authenticated shell');
  return value;
}
